import React from 'react';
import * as Icons from 'lucide-react';
import { MapPin, ImageIcon } from 'lucide-react';
import ResortGallery from './ResortGallery';

const DestinationChapter = ({ destination: d }) => {
  const cols = d.resorts.length === 1 ? 'md:grid-cols-1' : d.resorts.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3';
  const maxW = d.resorts.length === 1 ? 'max-w-2xl mx-auto' : '';

  return (
    <section id={d.id} className="bg-cream py-24 md:py-32 border-b border-divider relative">
      <div className="absolute top-8 left-6 md:left-10 chapter-badge text-gold uppercase">
        <span className="inline-block bg-gold text-warm px-2 py-1 mr-2">{d.chapter}</span>
        Chapter
      </div>

      <div className="max-w-[1300px] mx-auto px-6 md:px-10">
        {/* Top section — country intro + hero image */}
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div className="reveal">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-gold" />
              <span className="chapter-badge text-gold uppercase">Destination</span>
            </div>
            <h2 className="font-serif-display text-navy text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.95] font-medium">
              {d.country}
            </h2>
            <p className="mt-6 chapter-badge text-gold uppercase whitespace-pre-line leading-6">
              {d.tagline}
            </p>
            <div className="mt-6 h-px w-16 bg-gold/60" />
            <p className="mt-6 font-serif-editorial text-navy/85 text-lg md:text-xl leading-relaxed max-w-lg">
              {d.intro}
            </p>

            <div className="mt-8 border border-divider bg-warm px-5 py-5 max-w-sm">
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-gold" />
                <span className="chapter-badge text-gold uppercase">Location</span>
              </div>
              <div className="mt-2 font-serif-display text-navy text-xl">{d.location}</div>
              <p className="mt-2 font-sans-body text-navy/70 text-[0.9rem] leading-relaxed">
                {d.locationDesc}
              </p>
            </div>
          </div>

          <div className="reveal relative">
            <div className="relative aspect-[4/5] md:aspect-[5/6] overflow-hidden">
              <img
                src={d.heroImage}
                alt={d.country}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:flex flex-col items-start bg-warm border border-divider p-5 max-w-xs shadow-[0_20px_40px_-20px_rgba(15,35,64,0.2)]">
              <div className="chapter-badge text-gold uppercase">Every journey</div>
              <div className="mt-2 font-serif-editorial italic text-navy text-lg leading-snug">
                &ldquo;{d.pull}&rdquo;
              </div>
            </div>
          </div>
        </div>

        {/* Resorts */}
        <div className="mt-24 text-center reveal">
          <div className="gold-divider mb-4">
            <span className="chapter-badge text-gold uppercase">Choose Your Karma Experience in {d.country}</span>
          </div>
          <p className="font-serif-editorial text-navy/75 text-lg md:text-xl">
            {d.resorts.length === 1
              ? 'One curated retreat. One unforgettable stay.'
              : `${d.resorts.length} unique resorts. ${d.resorts.length} distinct experiences. One unforgettable destination.`}
          </p>
        </div>

        <div className={`mt-12 grid grid-cols-1 ${cols} gap-8 ${maxW}`}>
          {d.resorts.map((r) => (
            <ResortCard key={r.name} resort={r} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ResortCard = ({ resort: r }) => {
  const galleryCount = (r.gallery && r.gallery.length) || 1;
  return (
    <article className="resort-card bg-warm border border-divider overflow-hidden reveal flex flex-col h-full">
      <ResortGallery
        resort={r}
        trigger={
          <div className="relative aspect-[16/11] overflow-hidden group cursor-zoom-in">
            <img
              src={r.image}
              alt={r.name}
              className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute top-3 left-3 bg-gold text-warm chapter-badge uppercase px-2 py-1">
              {r.n}
            </div>
            <div className="absolute top-3 right-3 bg-warm/90 text-navy chapter-badge uppercase px-2 py-1">
              {r.nights}
            </div>
            {/* Gallery overlay hint */}
            <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/25 transition-colors duration-500 flex items-end justify-end p-3">
              <span className="inline-flex items-center gap-2 chapter-badge uppercase bg-warm/95 text-navy border border-divider px-3 py-1.5 shadow-sm transition-transform duration-500 group-hover:-translate-y-0.5">
                <ImageIcon size={12} />
                View Gallery · {galleryCount}
              </span>
            </div>
          </div>
        }
      />

      <div className="px-6 md:px-7 pt-6 pb-2 text-center">
        <h3 className="font-serif-display text-gold text-2xl md:text-[1.65rem]">{r.name}</h3>
        <div className="mt-1 chapter-badge text-navy/70 uppercase">{r.place}</div>
        <p className="mt-4 font-sans-body text-navy/75 text-[0.95rem] leading-relaxed">
          {r.desc}
        </p>
      </div>

      <div className="px-6 md:px-7 mt-auto pt-5 grid grid-cols-4 gap-3">
        {r.features.map((f) => {
          const Icon = Icons[f.icon] || Icons.Sparkles;
          return (
            <div key={f.label} className="flex flex-col items-center text-center">
              <div className="w-9 h-9 border border-gold/40 rounded-full flex items-center justify-center">
                <Icon size={15} className="text-gold" strokeWidth={1.4} />
              </div>
              <div className="mt-2 text-[0.68rem] uppercase tracking-wider text-navy/70 leading-tight">
                {f.label}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 bg-gold text-warm text-center py-4">
        <div className="chapter-badge uppercase opacity-80">Perfect For</div>
        <div className="mt-1 font-serif-editorial italic text-warm text-[1.05rem]">{r.perfectFor}</div>
      </div>
    </article>
  );
};

export default DestinationChapter;
